// <IMPORTANT>
// This block has to be the VERY FIRST THING WE RUN for automatic instrumentation
// to work as expected. It registers require-in-the-middle hooks that then wraps
// the methods of the SDKs of the dependencies. Registering such hooks after
// dependency has been already imported will have no effect.
import { initObservability } from "@wisnio/observability/browser";
import { initFeatureFlags } from "@wisnio/feature-flags/browser";
import { reactPlugin } from "@datadog/browser-rum-react";
const APPLICATION_NAME = "assessio-client";
const obs = initObservability(APPLICATION_NAME, {
  applicationId: import.meta.env.VITE_DD_RUM_APPLICATION_ID,
  clientToken: import.meta.env.VITE_DD_RUM_CLIENT_TOKEN,
  env: import.meta.env.VITE_DD_RUM_ENV,
  version: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  trackUserInteractions: ["true", "1", "on", "yes", "enabled"].includes(
    (import.meta.env.VITE_DD_RUM_TRACK_INTERACTIONS ?? "").trim().toLowerCase()
  ),
  sessionReplaySampleRate: Number(
    import.meta.env.VITE_DD_RUM_REPLAY_SAMPLE_RATE ?? 0
  ),
  sessionSampleRate: Number(
    import.meta.env.VITE_DD_RUM_SESSION_SAMPLE_RATE ?? 0
  ),
  // Values: allow / mask / mask-user-input / mask-unless-allowlisted
  defaultPrivacyLevel: import.meta.env.VITE_DD_RUM_PRIVACY_LEVEL,
  plugins: [reactPlugin({ router: false })],
});
if (obs) {
  initFeatureFlags(obs.flagsProvider);
}
// </IMPORTANT>
import { initLogger } from "@wisnio/logger/browser";
initLogger(APPLICATION_NAME, {
  clientToken: import.meta.env.VITE_DD_RUM_CLIENT_TOKEN,
  env: import.meta.env.VITE_DD_RUM_ENV,
  version: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
});

import React, { Suspense, use } from "react";
import ReactDOM from "react-dom/client";
import { ApolloProvider } from "@apollo/client";
import { TourProvider } from "@reactour/tour";

import { AppProvider } from "@/shared/contexts/app.context";
import { FeatureFlagProvider } from "@/shared/contexts/feature-flag.context";
import { initializeApolloClient } from "./shared/services/api/graphql/index";
import Routes from "./router";
import { FullPageLoaderOverlay } from "@/shared/ui/FullPageLoaderOverlay";

// Styles
import "./style/theme.css";
import "style/fontawesome/all.css";
import "./i18n";
import ErrorBoundary from "@/shared/widgets/ErrorBoundary";

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container);

const clientPromise = initializeApolloClient();

function App() {
  const client = use(clientPromise);

  return (
    <ApolloProvider client={client}>
      <FeatureFlagProvider>
        <TourProvider disableInteraction>
          <AppProvider>
            <Routes />
          </AppProvider>
        </TourProvider>
      </FeatureFlagProvider>
    </ApolloProvider>
  );
}

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <FullPageLoaderOverlay />
      <Suspense fallback={null}>
        <App />
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>
);
